import { useCallback, useEffect, useState } from 'react';

const KEY = 'wikistar:media-pref';

interface MediaPreferences {
  /**
   * Whether to also publish a NIP-94 (kind 1063) file-metadata event to
   * Nostr for each uploaded file.
   *
   * Deliberately **opt-in**: a NIP-94 event publicly announces the file
   * on whatever relays the user publishes to. The benefit is portability
   * — other clients, and the user's own other devices, can discover the
   * file — but that's a tradeoff that must be chosen explicitly, not a
   * default.
   */
  publishNip94: boolean;
}

function readPreferences(): MediaPreferences {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { publishNip94: false };
    const value = JSON.parse(raw) as Partial<MediaPreferences>;
    return { publishNip94: Boolean(value?.publishNip94) };
  } catch {
    return { publishNip94: false };
  }
}

const listeners = new Set<(prefs: MediaPreferences) => void>();
let current = readPreferences();

/**
 * A tiny opt-in preference for whether uploads also publish NIP-94
 * metadata events. Kept separate from `useLocalStorage` so the toggle on
 * the Media page and the check in `useMediaLibrary` always agree via
 * this module-level cache (the generic hook would keep two independent
 * copies that could drift).
 */
export function useMediaPreferences(): {
  publishNip94: boolean;
  setPublishNip94: (value: boolean) => void;
} {
  const [prefs, setPrefs] = useState(current);

  useEffect(() => {
    const listener = (next: MediaPreferences) => setPrefs(next);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const setPublishNip94 = useCallback((value: boolean) => {
    current = { publishNip94: value };
    try {
      localStorage.setItem(KEY, JSON.stringify(current));
    } catch {
      // Preferences are recoverable; a failed write just means it reverts
      // to the default next session rather than erroring visibly.
    }
    for (const listener of listeners) listener(current);
  }, []);

  return { publishNip94: prefs.publishNip94, setPublishNip94 };
}
